import { useEffect, useState } from "react";
import { getUserType } from "util/auth";
import {
  Category,
  Shop,
  OrderLargeClassification,
  Supplier,
  TyreOptions,
  WheelOptions,
  StorehouseSupplierOptionsWithBlank,
  OrderStatus,
  OrderReceiptStatus,
  HasRepresentativeCd,
  WholeShop,
  Subclass,
  OrderStatusObject,
} from "../types";
import { categories } from "../constants";
import getTyreOptionsApi from "../api/getTyreOptionsApi";
import getWholeShopOptionsApi from "../api/getWholeShopOptionsApi";
import getShopOptionsApi from "../api/getShopOptionsApi";
import getStockTyreOptionsApi from "../api/getStockTyreOptionsApi";

/**
 * 注文検索hooks
 * @param needStoreOptions 倉庫、仕入先のプルダウンも取得するかどうか
 * @param defaultStorehouse デフォルトの倉庫名
 * @param defaultSupplier デフォルトの仕入先名
 * @param isSupplier 仕入債受注かどうか
 * @returns JSX.Element
 */
export default function useOrderSearch(
  needStoreOptions: boolean,
  defaultStorehouse?: string | null,
  defaultSupplier?: string | null,
  isSupplier?: boolean,
) {
  const defaultStorehouseSupplier: StorehouseSupplierOptionsWithBlank = (() => {
    if (defaultStorehouse) return "倉庫";
    if (defaultSupplier) return "仕入先";
    return "";
  })();
  const [orderNumber, setOrderNumber] = useState("");
  const [productCd, setProductCd] = useState("");
  const [productName, setProductName] = useState("");
  const [brand, setBrand] = useState("");
  const [janCd, setjanCd] = useState("");
  const [productNumber, setProductNumber] = useState("");
  const [tyreWidth, setTyreWidth] = useState<number | undefined>(undefined);
  const [tyreRatio, setTyreRatio] = useState<number | undefined>(undefined);
  const [tyreRimDiam, setTyreRimDiam] = useState<number | undefined>(undefined);
  const [wheelWidth, setWheelWidth] = useState<number | undefined>(undefined);
  const [wheelPcd, setWheelPcd] = useState<number | undefined>(undefined);
  const [wheelHole, setWheelHole] = useState<number | undefined>(undefined);
  const [wheelRimDiam, setWheelRimDiam] = useState<number | undefined>(undefined);
  const companyCd: string | undefined = localStorage.getItem("company_cd") ?? undefined;
  const isClient = getUserType() === "C0003";
  const [shopCd, setShopCd] = useState<string>(localStorage.getItem("shop_cd") ?? "");
  const [classification, setClassification] = useState("");
  const [storehouseSupplierWithBlank, setStorehouseSupplierWithBlank] =
    useState<StorehouseSupplierOptionsWithBlank>(defaultStorehouseSupplier);
  const [storehouse, setStorehouse] = useState(defaultStorehouse ?? "");
  const [supplier, setSupplier] = useState(defaultSupplier ?? ""); // 仕入先名
  const [tyreOptions, setTyreOptions] = useState<TyreOptions>({
    tyreWidthList: [],
    tyreRatioList: [],
    tyreRimDiamList: [],
  });
  const [wheelOptions, setWheelOptions] = useState<WheelOptions>({
    wheelWidthList: [],
    wheelPcdList: [],
    wheelHoleList: [],
    wheelRimDiamList: [],
  });
  const [classificationOptions, setClassificationOptions] = useState<OrderLargeClassification[]>(
    [],
  );
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [shopOptions, setShopOptions] = useState<Shop[]>([]);
  const [wholeShopOptions, setWholeShopOptions] = useState<WholeShop[]>([]); // 本部用注文作成・編集画面の注文作成モーダルで店舗の候補に使用する。得意先も含めて全件入る。
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState<Category>(categories[1]);

  // 追加
  const [isRepresentativeCd, setIsRepresentativeCd] =
    useState<HasRepresentativeCd>("全て");
  const [isCustomerOrder, setIsCustomerOrder] = useState<boolean | undefined>(undefined); // false:無し、true:あり、undefined:両方
  const [shopName, setShopName] = useState("");
  const [orderStatus, setOrderStatus] = useState<OrderStatus>("");
  const [orderReceiptStatus, setOrderReceiptStatus] = useState<OrderReceiptStatus>("");
  const [storeOrderer, setStoreOrderer] = useState("");
  const [approver, setApprover] = useState("");
  const [orderStartDate, setOrderStartDate] = useState("");
  const [orderEndDate, setOrderEndDate] = useState("");
  const [companyCdField, setCompanyCdField] = useState(""); // 検索フィールドに入力した企業コード。企業ユーザーの場合にcompanyCdを使っているため、例外的にこの名前にした。
  const [companyName, setCompanyName] = useState("");
  const [centerCd, setCenterCd] = useState("");
  const [orderStatusOptions, setOrderStatusOptions] = useState<OrderStatusObject[]>([]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const data = await getTyreOptionsApi(needStoreOptions, companyCd ?? undefined);
        if (companyCd) {
          const wsOptions = await getShopOptionsApi(companyCd);
          const sOptions  = await getStockTyreOptionsApi(false, companyCd);
          setWholeShopOptions(wsOptions);
          setShopOptions(sOptions.shopOptions.filter(so => so.shopCd != null)); // 注文では企業の得意先は対象外
        } else if (shopCd || isSupplier || isClient) {
          setWholeShopOptions([])
        } else {
          const wsOptions = await getWholeShopOptionsApi();
          setWholeShopOptions(wsOptions);
        }
        setTyreOptions(data.tyreOptions);
        setWheelOptions(data.wheelOptions);
        setClassificationOptions(data.classificationOptions);
        setBrandOptions(data.brandOptions);
        setOrderStatusOptions(data.statusOptions);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return [
    {
      isRepresentativeCd,
      isCustomerOrder,
      shopName,
      orderStatus,
      orderReceiptStatus,
      storeOrderer,
      approver,
      orderStartDate,
      orderEndDate,
      companyCdField,
      companyName,
      centerCd,
      orderNumber,
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
      category,
      shopCd,
      companyCd,
    },
    {
      setIsRepresentativeCd,
      setIsCustomerOrder,
      setShopName,
      setOrderStatus,
      setOrderReceiptStatus,
      setStoreOrderer,
      setApprover,
      setOrderStartDate,
      setOrderEndDate,
      setCompanyCdField,
      setCompanyName,
      setCenterCd,
      setOrderNumber,
      setProductCd,
      setProductName,
      setBrand,
      setjanCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
      setShopCd,
    },
    {
      classificationOptions,
      tyreOptions,
      wheelOptions,
      brandOptions,
      shopOptions,
      wholeShopOptions,
      orderStatusOptions,
    },
    { isLoading },
  ] as const;
}
